import {StorageUtility} from './Storage';
import {Locale} from './Locale';
import {Affiliate} from './Affiliate';
import {UI} from './UI';
import {GDPR} from './GDPR';
import {Marketing} from './Marketing';

declare var window: any;
declare var Windows: any;
declare var $: any;

interface NavigatorUAData {
    platform: string;
}

interface NavigatorWithUAData extends Navigator {
    userAgentData?: NavigatorUAData;
}

export class Environment {
    private static instance: Environment;

    static getInstance(): Environment {
        if (!Environment.instance) {
            Environment.instance = new Environment();
        }
        return Environment.instance;
    }

    public getEnv() {
        const hostname = new URL(window.keeperPreload.apiEndpoint).hostname;
        if (hostname === 'dev.keepersecurity.com') {
            return 'dev';
        } else if (hostname === 'qa.keepersecurity.com') {
            return 'qa';
        } else if (hostname === 'staging.keepersecurity.com') {
            return 'staging';
        } else if (hostname === 'keepersecurity.com') {
            return 'prod';
        } else if (hostname === 'localhost') {
            return 'local';
        }
    }

    public whatOS() {
        const nav = typeof navigator !== 'undefined' ? (navigator as NavigatorWithUAData) : null;

        const ua = nav?.userAgent || '';
        const platform = nav?.userAgentData?.platform || '';

        // Prefer modern User-Agent Client Hints
        if (platform) {
            const platformLower = platform.toLowerCase();
            if (platformLower.includes('windows')) return 'win';
            if (platformLower.includes('mac')) return 'mac';
            if (platformLower.includes('linux') && !platformLower.includes('android')) return 'nix';
            if (platformLower.includes('android')) return 'Android';
            if (platformLower.includes('iphone')) return 'iPhone';
            if (platformLower.includes('ipad')) return 'iPad';
        }

        // Fallback to legacy userAgent detection
        if (/BlackBerry|BB10/i.test(ua)) {
            return 'BlackBerry';
        }

        if (
            /Silk/i.test(ua) ||
            (/Fire/i.test(ua) && !/Firefox/i.test(ua)) ||
            /KFTT|KFOT|KFJWA|KFJWI|KFSOWI|KFTHWA|KFTHWI|KFAPWA|KFAPWI/i.test(ua)
        ) {
            return 'Kindle';
        }

        if (/Windows Phone/i.test(ua)) {
            return 'WinPhone';
        }

        if (/iPhone/i.test(ua)) {
            return 'iPhone';
        }

        if (/iPad/i.test(ua)) {
            return 'iPad';
        }

        if (/Android/i.test(ua)) {
            return 'Android';
        }

        if (/Mac OS X 10[._](5|6|7)/.test(ua)) {
            return 'oldmac';
        }

        if (/Macintosh|Mac OS X/i.test(ua)) {
            return 'mac';
        }

        if (/Windows NT/i.test(ua)) {
            return 'win';
        }

        if (/Linux/i.test(ua) && !/Android/i.test(ua) && !/Mac/i.test(ua)) {
            return 'nix';
        }

        return 'NA';
    }

    public whatBrowser() {
        if ((window.navigator.brave && window.navigator.brave.isBrave()) || false) {
            return 'brave';
        }
        const ua = (window.navigator && window.navigator.userAgent) || '';
        if (ua.indexOf('Edg/') !== -1) {
            return 'chromium-edge';
        }
        if (ua.indexOf('Edge') !== -1) {
            return 'edge';
        }
        if (ua.indexOf('Opera') !== -1 || ua.indexOf('OPR') !== -1) {
            return 'opera';
        }
        if (ua.indexOf('Chrome') !== -1) {
            return 'chrome';
        }
        if (ua.indexOf('Safari') !== -1) {
            return 'safari';
        }
        if (ua.indexOf('Firefox') !== -1) {
            return 'firefox';
        }
        if (ua.indexOf('MSIE') !== -1 || typeof Windows !== 'undefined') {
            return 'ie';
        }
        // IE 11 of course is completely different
        if (ua.indexOf('Trident') !== -1) {
            return 'ie';
        }
        return 'NA';
    }

    /**
     * @summary Gets the version for the IE Extension.
     * @param {function} callback - The function to be called on completion.
     * @since 12.0.0
     */
    public getBrowserExtensionVersionIE(callback) {
        $.getJSON(window.keeperPreload.apiEndpoint + '/ie_extension/ie_extension_version.txt')
            .done(json => {
                callback(json);
            })
            .fail(() => {
                callback('');
            });
    }

    /**
     * @summary Gets the version for the Desktop Electon keeper app.
     * @param {function} callback - The function to be called on completion.
     * @since 12.0.0
     */
    public getDesktopElectronVersion(callback) {
        $.getJSON(
            window.keeperPreload.downloadEndpoint + '/desktop_electron/desktop_electron_version.txt'
        )
            .done(json => {
                callback(json);
            })
            .fail(() => {
                // Use backup version file only if the first request fails
                $.getJSON('/desktop_electron_version.txt')
                    .done(backupJson => {
                        callback(backupJson);
                    })
                    .fail(() => {
                        callback('');
                    });
            });
    }

    public getBrowserExtensionVersionsStable(callback) {
        $.getJSON(window.keeperPreload.apiEndpoint + '/browser_extension/extension_version.json')
            .done(data => {
                callback(data);
            })
            .fail(() => {
                callback('');
            });
    }

    public getForcefieldVersion(callback) {
        $.getJSON(window.keeperPreload.downloadEndpoint + '/forcefield/version.json')
            .done(data => {
                callback(data);
            })
            .fail(() => {
                callback('');
            });
    }

    /**
     * @summary Returns request object.
     * @since 12.0.0
     * @returns {object} Returns request object.
     */
    public getRequestParams = (action = '', hubspotEnabled = true) => {
        // THIS METHOD SHOULD NOT BE USED, USE THE MORE SPECIFIC ONES
        // getHubspotParams
        // getDefaultParams

        try {
            const request: any = {};
            request.locale = Locale.getInstance().getCurrentLocale();
            request.leadSource = StorageUtility.getInstance().getLocal('lead_source');
            request.leadSourceUrl = StorageUtility.getInstance().getLocal('lead_source_params');

            if (action === 'freeTrial') {
                request.rakuten = Affiliate.getInstance().hasTrialOrderID() ? true : false;
                request.action = action;
            }

            if (
                (action === 'freeTrial' ||
                    action === 'whitePaper' ||
                    action === 'contactUs' ||
                    action === 'breachWatchScanB2B' ||
                    action === 'influencer_signup' ||
                    action === 'MSPPartnerSignup') &&
                hubspotEnabled
            ) {
                request.hubspot = {};
                request.hubspot.context = {};
                request.hubspot.context.hutk = '';
                request.hubspot.context.pageUri = window.location.href || '';
                request.hubspot.context.pageName = document.querySelector('title').text || '';

                if (StorageUtility.getInstance().isAvailable()) {
                    request.hubspot.context.hutk = StorageUtility.getInstance().getCookie(
                        'hubspotutk'
                    );
                }
            }

            return request;
        } catch (e) {
            console.warn(e);
            return {};
        }
    };

    /**
     * @summary Enables browser specific features/bugs.
     * @since 12.0.0
     */
    public fixBrowserCompatibilityIssues = () => {
        UI.getInstance().applyMaterialIconsFixForIE();

        // Mobile Safari Fix
        const ua = window.navigator.userAgent;
        const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
        const webkit = !!ua.match(/WebKit/i);
        const iOSSafari = iOS && webkit && !ua.match(/CriOS/i) && !ua.match(/OPiOS/i);

        if (iOSSafari) {
            $('html').addClass('mobile-safari');
        }
    };

    /**
     * @summary Runs cookie consent logic to determine whether user can store data or not.
     * @since 12.0.0
     */
    public checkCookieConsent = () => {
        if (StorageUtility.getInstance().isAvailable()) {
            GDPR.getInstance().setConsent('true');
        }
    };

    /**
     * @summary Sets the referrer in the lead_source_params for SalesForce.
     * @since 12.0.0
     */
    public setReferrer = () => {
        const referrer = document.referrer || '';

        // if referrer was not keeper
        let isAllowedReferrer = false;
        if (referrer.length > 0) {
            try {
                const refUrl = new URL(referrer);
                const hostname = refUrl.hostname;
                // Accept keepersecurity.com and its subdomains, and localhost
                if (
                    hostname === 'keepersecurity.com' ||
                    hostname.endsWith('.keepersecurity.com') ||
                    hostname === 'localhost'
                ) {
                    isAllowedReferrer = true;
                }
            } catch (e) {
                // If parsing fails, treat as not allowed
            }
        }
        if (!isAllowedReferrer && referrer.length > 0) {
            Marketing.getInstance().appendLead('referrer', encodeURIComponent(referrer));
        }
    };

    public isMobile = () => {
        if (window.matchMedia('(min-width: 768px)').matches) {
            return false;
        } else {
            return true;
        }
    };

    public isDesktop = () => {
        if (window.matchMedia('(min-width: 768px)').matches) {
            return true;
        } else {
            return false;
        }
    };
}

export default Environment;
